import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Upload, 
  FileJson, 
  FileCode, 
  AlertCircle, 
  CheckCircle
} from 'lucide-react';
import { machineApi } from '@/api/machine';

/**
 * Import Machine Dialog
 * 
 * Supports importing machines from:
 * - DSL text
 * - JSON (Machine IR format)
 * - File upload (.dsl, .json)
 */
const ImportMachineDialog = ({ open, onOpenChange, onImportSuccess }) => {
  const [activeTab, setActiveTab] = useState('dsl');
  const [dslInput, setDslInput] = useState('');
  const [jsonInput, setJsonInput] = useState('');
  const [importing, setImporting] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const fileInputRef = useRef(null);

  const normalizeCapability = (capability) => {
    if (typeof capability === 'string') {
      return { name: capability, constraints: {} };
    }

    return {
      name: capability?.name || 'Capability.Unknown',
      constraints: capability?.constraints || {},
    };
  };

  const normalizeGuard = (guard) => {
    if (typeof guard === 'string') {
      return { name: guard, predicate: guard, params: {} };
    }

    return {
      name: guard?.name || guard?.predicate || 'Guard',
      predicate: guard?.predicate || guard?.name || 'true',
      params: guard?.params || {},
    };
  };

  const normalizeInvariant = (invariant) => {
    if (typeof invariant === 'string') {
      return {
        name: invariant,
        assertion: invariant,
        message: invariant,
      };
    }

    return {
      name: invariant?.name || invariant?.assertion || 'Invariant',
      assertion: invariant?.assertion || invariant?.name || 'true',
      message: invariant?.message || invariant?.assertion || invariant?.name || 'Invariant failed',
    };
  };

  const normalizeState = (stateId, state = {}) => {
    const childStates = Object.entries(state.states || {}).map(([childId, childState]) => normalizeState(childId, childState));

    return [
      stateId,
      {
        id: state.id || stateId,
        type: state.type || 'atomic',
        initial: state.initial || null,
        states: Object.fromEntries(childStates),
        transitions: (state.transitions || [])
          .filter((transition) => transition?.event && transition?.target)
          .map((transition) => ({
            event: transition.event,
            target: transition.target,
            guards: (transition.guards || []).map(normalizeGuard),
            effects: transition.effects || [],
          })),
        entry: state.entry || [],
        exit: state.exit || [],
        invariants: (state.invariants || []).map(normalizeInvariant),
        meta: state.meta || {},
      },
    ];
  };

  const normalizeMachineIRPayload = (rawMachineIR) => {
    const stateEntries = Object.entries(rawMachineIR.states || {});
    if (stateEntries.length === 0) {
      throw new Error('Invalid machine IR: at least one state is required');
    }

    const machineId = rawMachineIR.id || `machine_${Date.now()}`;

    return {
      id: machineId,
      name: rawMachineIR.name || machineId,
      version: {
        version: rawMachineIR.version?.version || '1.0.0',
        created_at: rawMachineIR.version?.created_at || new Date().toISOString(),
        created_by: rawMachineIR.version?.created_by || null,
        parent_hash: rawMachineIR.version?.parent_hash || null,
        migration_applied: rawMachineIR.version?.migration_applied || null,
      },
      initial: rawMachineIR.initial || stateEntries[0][0],
      context_type: rawMachineIR.context_type || 'Record<string, any>',
      states: Object.fromEntries(stateEntries.map(([stateId, state]) => normalizeState(stateId, state))),
      events: (rawMachineIR.events || []).map((event) => ({
        name: event?.name || 'EVENT',
        payload_type: event?.payload_type || 'any',
        meta: event?.meta || {},
      })),
      effects: Object.fromEntries(
        Object.entries(rawMachineIR.effects || {}).map(([effectName, effect = {}]) => [
          effectName,
          {
            name: effect.name || effectName,
            input_type: effect.input_type || 'void',
            output_type: effect.output_type || 'void',
            failure_type: effect.failure_type || 'Error',
            required_capabilities: (effect.required_capabilities || effect.capabilities || []).map(normalizeCapability),
          },
        ])
      ),
      guards: Object.fromEntries(
        Object.entries(rawMachineIR.guards || {}).map(([guardName, guard = {}]) => [
          guardName,
          normalizeGuard({ ...guard, name: guard.name || guardName }),
        ])
      ),
      invariants: (rawMachineIR.invariants || []).map(normalizeInvariant),
      meta: rawMachineIR.meta || {},
    };
  };


  const handleDSLImport = async () => {
    if (!dslInput.trim()) {
      toast.error('Please enter DSL code');
      return;
    }

    setImporting(true);
    setParseResult(null);

    try {
      const result = await machineApi.createMachine({ dsl_source: dslInput });
      setParseResult({ success: true, machine: result });
      toast.success(`Machine "${result.name}" imported successfully!`);
      onImportSuccess?.(result);
      onOpenChange(false);
      setDslInput('');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      setParseResult({ success: false, error: detail });
      toast.error(`Import failed: ${detail}`);
    } finally {
      setImporting(false);
    }
  };

  const handleJSONImport = async () => {
    if (!jsonInput.trim()) {
      toast.error('Please enter JSON');
      return;
    }

    setImporting(true);
    setParseResult(null);

    try {
      const machineIR = JSON.parse(jsonInput);
      const normalizedMachineIR = normalizeMachineIRPayload(machineIR);
      const result = await machineApi.createMachine({ ir_json: normalizedMachineIR });

      setParseResult({ success: true, machine: result });
      toast.success(`Machine "${result.name}" imported successfully!`);
      onImportSuccess?.(result);
      onOpenChange(false);
      setJsonInput('');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Invalid JSON';
      setParseResult({ success: false, error: detail });
      toast.error(`Import failed: ${detail}`);
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      if (typeof content === 'string') {
        if (file.name.endsWith('.json')) {
          setActiveTab('json');
          setJsonInput(content);
        } else {
          setActiveTab('dsl');
          setDslInput(content);
        }
        toast.success(`Loaded ${file.name}`);
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsText(file);
  };

  const loadExample = () => {
    const example = `machine ExampleMachine {
    initial state Idle
    
    effect FetchData requires [Net.HTTP]
    effect SaveData requires [Storage.Local]
    
    guard IsValid
    
    state Idle {
        on START -> Loading { effect FetchData }
    }
    
    state Loading {
        on SUCCESS [guard IsValid] -> Ready { effect SaveData }
        on FAILURE -> Error {}
    }
    
    state Ready {
        on RESET -> Idle {}
    }
    
    state Error {
        on RETRY -> Loading { effect FetchData }
        on DISMISS -> Idle {}
    }
}`;
    setDslInput(example);
    setActiveTab('dsl');
    toast.success('Example loaded');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Upload className="w-5 h-5" />
            <span>Import State Machine</span>
          </DialogTitle>
          <DialogDescription>
            Import a state machine from DSL code, JSON, or file upload.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="dsl" className="flex items-center space-x-2">
              <FileCode className="w-4 h-4" />
              <span>DSL</span>
            </TabsTrigger>
            <TabsTrigger value="json" className="flex items-center space-x-2">
              <FileJson className="w-4 h-4" />
              <span>JSON</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dsl" className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>DSL Source</Label>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={loadExample}
                    className="h-7 text-xs"
                  >
                    Load Example
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => fileInputRef.current?.click()}
                    className="h-7 text-xs"
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    Upload .dsl
                  </Button>
                </div>
              </div>
              <Textarea
                value={dslInput}
                onChange={(e) => setDslInput(e.target.value)}
                placeholder={`machine MyMachine {
    initial state Idle
    
    state Idle {
        on START -> Active {}
    }
    
    state Active {
        on STOP -> Idle {}
    }
}`}
                className="font-mono text-sm h-64 bg-black/30"
                data-testid="dsl-import-input"
              />
            </div>

            <Button 
              onClick={handleDSLImport} 
              disabled={importing || !dslInput.trim()}
              className="w-full"
            >
              {importing ? 'Importing...' : 'Import from DSL'}
            </Button>
          </TabsContent>

          <TabsContent value="json" className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Machine IR (JSON)</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => fileInputRef.current?.click()}
                  className="h-7 text-xs"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  Upload .json
                </Button>
              </div>
              <Textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder='{"name": "MyMachine", "initial": "Idle", "states": {...}}'
                className="font-mono text-sm h-64 bg-black/30"
                data-testid="json-import-input"
              />
            </div>

            <Button 
              onClick={handleJSONImport} 
              disabled={importing || !jsonInput.trim()}
              className="w-full"
            >
              {importing ? 'Importing...' : 'Import from JSON'}
            </Button>
          </TabsContent>
        </Tabs>

        {/* Parse Result */}
        {parseResult && (
          <div className={`mt-4 p-3 rounded border ${
            parseResult.success 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-center space-x-2">
              {parseResult.success ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400">
                    Successfully imported: {parseResult.machine?.name}
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-400">
                    {parseResult.error}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".dsl,.json,.txt"
          onChange={handleFileUpload}
          className="hidden"
        />
      </DialogContent>
    </Dialog>
  );
};

export default ImportMachineDialog;
